import { useMemo } from 'react';

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { CustomNotification } from 'common/components';
import { saveQaSpecDataClaims } from 'services/product';
import { getKeyProductSpec } from 'pages/qa-spec/hooks/useProductSpecInfo';
import { forwardTo } from 'utils/common/route';

import * as productServices from 'services/product';
import { getQaPackagingTabDataQueryKey } from 'pages/qa-spec/components/qa-packaging/queries';
import { keyGetProductItemModules } from 'pages/product-full-view/shared/hooks';
import {
  generateDefaultQaClaims,
  generateFormValuesClaims,
} from 'pages/qa-spec/components/qa-claims/utils';

import { useGetEnumsByEnumName, useGetMemberId } from 'hooks';

export const getQaClaimsTabDataQueryKey = (productId, productType) => {
  return ['qa-spec', 'qa-spec-data-claims', parseInt(productId), productType ?? ''];
};

export const useGetQaClaimsTabData = ({ productId, productType }, options) => {
  const queryClient = useQueryClient();

  const requiredClaims = []; //useGetEnumsByEnumName('OptionalClaims');
  const queries = useQuery({
    queryKey: getQaClaimsTabDataQueryKey(productId, productType),
    queryFn: async () => {
      const response = await productServices.getQaClaimsData({
        productId,
        productType,
      });
      if (response?.isSuccess) {
        return response?.data;
      } else {
        CustomNotification.error(response?.message ?? 'Something went wrong!');
        forwardTo('/products');
      }
    },
    ...options,
  });

  const invalidateClaims = () => {
    queryClient.invalidateQueries({
      queryKey: getQaClaimsTabDataQueryKey(productId, productType),
    });
  };

  const defaultQaClaims = useMemo(() => {
    return generateDefaultQaClaims(queries?.data, requiredClaims);
  }, [queries?.data, requiredClaims]);

  return {
    ...queries,
    invalidateClaims,
    data: useMemo(
      () => generateDefaultQaClaims(queries?.data, requiredClaims),
      [queries?.data, requiredClaims]
    ),
    objectClaims: useMemo(() => {
      return generateFormValuesClaims(defaultQaClaims?.claims || []);
    }, [defaultQaClaims]),
  };
};

export const useSaveQaClaimsTab = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params) => {
      const response = await saveQaSpecDataClaims(params);
      const { isSuccess, data, message } = response || {};

      if (isSuccess) {
        return Promise.resolve(data);
      } else if (message) {
        return Promise.reject(message);
      }
    },
    onSuccess: (data, { productId }) => {
      queryClient.invalidateQueries({
        queryKey: getQaClaimsTabDataQueryKey(productId),
      });
      //* sync to qa product spec
      queryClient.invalidateQueries({
        queryKey: getKeyProductSpec(productId),
      });
      //* sync to product labeling
      queryClient.invalidateQueries({
        queryKey: getQaPackagingTabDataQueryKey(productId),
      });
      //* sync to product detail view
      queryClient.invalidateQueries({
        queryKey: keyGetProductItemModules(productId),
      });
      CustomNotification.success('Update Claim data successfully!');
    },
    onError: (error) => {
      CustomNotification.error(error || 'Server Error');
    },
  });
};

export const getSetupOptionalClaimsKey = (memberId, productType) => {
  return ['qa-spec', 'setup-optional-claims', parseInt(memberId), productType];
};

export const useGetSetupOptionalClaims = (productType, productId, memberId) => {
  // const { memberId } = useGetMemberId();
  const queryClient = useQueryClient();
  const queries = useQuery({
    queryKey: getSetupOptionalClaimsKey(memberId, productType),
    queryFn: async () => {
      const response = await productServices.getSetupOptionalClaims({
        memberId,
        productType,
        productId
      });
      if (response?.isSuccess) {
        return response?.data;
      } else {
        CustomNotification.error(response?.message ?? 'Something went wrong!');
        forwardTo('/products');
      }
    },
  });

  const invalidateSetupOptionalClaims = () => {
    queryClient.invalidateQueries({
      queryKey: getSetupOptionalClaimsKey(memberId, productType),
    });
  };

  return {
    invalidateSetupOptionalClaims,
    setupOptionalClaims: queries?.data?.optionalClaims || [],
    productLevelClaims: queries?.data?.productLevelClaims || [],
    isLoading: queries.isLoading,
  };
};

export const useGetReadyToUseClaim = ({ productId, productType, claimTypeCode }, options) => {
  const queryClient = useQueryClient();

  const queries = useQuery({
    queryKey: ['readyToUseClaim', productId, productType, claimTypeCode],
    queryFn: async () => {
      const response = await productServices.getQaClaimsData({ productId, productType });
      if (response?.isSuccess) {
        return response?.data?.claims?.find(c => c.claimTypeCode === claimTypeCode) || null;
      } else {
        CustomNotification.error(response?.message ?? 'Something went wrong!');
        return null;
      }
    },
    enabled: false, 
    ...options,
  });

  const invalidateReadyToUseClaims = () => {
    queryClient.invalidateQueries({
      queryKey: ['readyToUseClaim', productId, productType, claimTypeCode],
    });
  };

  return {
    ...queries,
    invalidateReadyToUseClaims,
    data: queries.data
  };
};
